// Service pillars detail content + routing map + booking copy
// Separated from the main i18n.js to keep it manageable.

export const SERVICE_ROUTES = {
  platforms: 'plataformas',
  ai: 'ia',
  security: 'seguridad',
  blockchain: 'blockchain',
  banking: 'banca',
  cauracademy: 'cauracademy',
};

// Map from URL slug back to product id
export const SLUG_TO_ID = Object.fromEntries(
  Object.entries(SERVICE_ROUTES).map(([k, v]) => [v, k])
);

export const serviceDetails = {
  es: {
    platforms: {
      id: 'platforms',
      slug: 'plataformas',
      kicker: '// Plataformas a Medida',
      title: 'Software enterprise y gubernamental, hecho a tu medida',
      subtitle: 'Diseñamos, construimos y operamos plataformas críticas para instituciones que no pueden fallar.',
      capabilities: [
        { title: 'Arquitectura cloud-native', desc: 'Microservicios escalables, contenedores y orquestación con Kubernetes.' },
        { title: 'Integración con legacy', desc: 'Conectamos tus sistemas existentes mediante APIs seguras sin romper operaciones.' },
        { title: 'Diseño centrado en usuario', desc: 'Interfaces claras para equipos operativos, decisores y ciudadanos.' },
        { title: 'Alta disponibilidad', desc: 'SLA 99.9%+, observabilidad completa y planes de contingencia.' },
      ],
      use_cases: [
        { title: 'Portales ciudadanos', desc: 'Tramites, consultas y servicios públicos digitalizados end-to-end.' },
        { title: 'Dashboards ejecutivos', desc: 'Visibilidad en tiempo real de KPIs críticos para la toma de decisiones.' },
        { title: 'Automatización back-office', desc: 'Reducción de tareas manuales con flujos digitales y reportes.' },
      ],
      methodology: [
        { title: 'Discovery', desc: 'Entendemos negocio, stakeholders, riesgos y dependencias.' },
        { title: 'Arquitectura', desc: 'Diseñamos solución con seguridad y escalabilidad desde el día 1.' },
        { title: 'Desarrollo', desc: 'Sprints cortos, demos continuas, revisión de código estricta.' },
        { title: 'Operación', desc: 'Hand-off documentado + soporte 24/7 si lo necesitas.' },
      ],
      tech: ['React', 'FastAPI', 'Node.js', 'PostgreSQL', 'MongoDB', 'Kubernetes', 'Docker', 'API-first', 'Cloud Native'],
    },
    ai: {
      id: 'ai',
      slug: 'ia',
      kicker: '// IA & Automatización',
      title: 'Inteligencia artificial aplicada a procesos críticos',
      subtitle: 'Modelos y agentes que automatizan flujos complejos, reducen errores y liberan horas de tu equipo.',
      capabilities: [
        { title: 'Agentes inteligentes', desc: 'LLMs orquestados con herramientas corporativas — decisiones asistidas en tiempo real.' },
        { title: 'Automatización de procesos', desc: 'Flujos documentales, back-office y operaciones con IA + RPA.' },
        { title: 'Modelos personalizados', desc: 'Entrenamos modelos por industria con tus datos — privados y bajo tu control.' },
        { title: 'Analytics predictivo', desc: 'Forecasting, detección de anomalías y segmentación inteligente.' },
      ],
      use_cases: [
        { title: 'KYC / onboarding', desc: 'Verificación automática con OCR + modelos de fraude.' },
        { title: 'Atención asistida', desc: 'Co-pilotos IA para equipos de soporte y ventas.' },
        { title: 'Clasificación documental', desc: 'Procesamos miles de documentos por día sin fatiga humana.' },
      ],
      methodology: [
        { title: 'Oportunidad', desc: 'Identificamos dónde la IA paga más rápido.' },
        { title: 'Dataset seguro', desc: 'Curación y anonimización de datos con políticas estrictas.' },
        { title: 'Piloto', desc: 'Medimos ROI antes de escalar — resultados en semanas.' },
        { title: 'Producción', desc: 'Monitoreo continuo del modelo y re-entrenamiento periódico.' },
      ],
      tech: ['LLMs', 'Python', 'PyTorch', 'OpenAI / Anthropic', 'Vector DBs', 'LangChain', 'RAG', 'MLOps'],
    },
    security: {
      id: 'security',
      slug: 'seguridad',
      kicker: '// Seguridad Gov-Grade',
      title: 'Estándares gubernamentales aplicados a tu stack',
      subtitle: 'Zero-Trust, hardening continuo y cumplimiento de las normas más exigentes.',
      capabilities: [
        { title: 'Arquitectura Zero-Trust', desc: 'Ninguna confianza implícita. Autenticación y autorización en cada petición.' },
        { title: 'Cumplimiento', desc: 'ISO 27001, NIST, SOC 2 — acompañamos todo el camino de certificación.' },
        { title: 'Hardening & auditorías', desc: 'Pentesting continuo, revisión de código y endurecimiento de infraestructura.' },
        { title: 'Gestión de identidad', desc: 'SSO, MFA, RBAC granular y detección de anomalías.' },
      ],
      use_cases: [
        { title: 'Plataformas financieras', desc: 'Protección de transacciones y datos sensibles.' },
        { title: 'Sistemas gubernamentales', desc: 'Datos ciudadanos y servicios críticos bajo llave.' },
        { title: 'Respuesta a incidentes', desc: 'Planes de contingencia y simulacros programados.' },
      ],
      methodology: [
        { title: 'Assessment', desc: 'Mapeamos superficie de ataque y brechas.' },
        { title: 'Roadmap', desc: 'Plan priorizado por impacto y esfuerzo.' },
        { title: 'Implementación', desc: 'Controles técnicos + políticas + training.' },
        { title: 'Mejora continua', desc: 'Auditorías periódicas y threat intel.' },
      ],
      tech: ['Zero-Trust', 'OAuth 2.0', 'SAML', 'Vault', 'ISO 27001', 'NIST', 'OWASP', 'SIEM', 'WAF'],
    },
    blockchain: {
      id: 'blockchain',
      slug: 'blockchain',
      kicker: '// Blockchain & Web3',
      title: 'Nuestro ADN desde 2016, aplicado a cualquier industria',
      subtitle: 'Smart contracts, tokenización e identidad descentralizada para casos de uso reales.',
      capabilities: [
        { title: 'Smart contracts auditables', desc: 'Diseñados con patrones seguros y auditorías formales.' },
        { title: 'Tokenización', desc: 'Activos reales (RWA), equity y programas de lealtad on-chain.' },
        { title: 'Identidad descentralizada', desc: 'DIDs y credenciales verificables sin fricción.' },
        { title: 'Integración cross-chain', desc: 'Puentes seguros y multi-cadena cuando el caso lo requiere.' },
      ],
      use_cases: [
        { title: 'Trazabilidad', desc: 'Cadenas de suministro auditables end-to-end.' },
        { title: 'Programas de lealtad', desc: 'Tokens con valor real para tus clientes.' },
        { title: 'Registros públicos', desc: 'Inmutables, transparentes y verificables.' },
      ],
      methodology: [
        { title: 'Caso de uso', desc: 'Validamos si blockchain suma valor real.' },
        { title: 'Arquitectura', desc: 'Elección de red, estándares y modelo económico.' },
        { title: 'Desarrollo & auditoría', desc: 'Código seguro + auditoría formal.' },
        { title: 'Deployment', desc: 'Lanzamiento, monitoreo on-chain y mantenimiento.' },
      ],
      tech: ['Solidity', 'Ethereum', 'Bitcoin', 'Polygon', 'IPFS', 'The Graph', 'Hardhat', 'Foundry'],
    },
    banking: {
      id: 'banking',
      slug: 'banca',
      kicker: '// Soluciones Bancarias',
      title: 'Aliados de la banca nacional en Venezuela',
      subtitle: 'Tecnología financiera con integraciones seguras, cumplimiento y experiencia real en el sector.',
      capabilities: [
        { title: 'Integración core-banking', desc: 'APIs hacia sistemas core y reguladores — confiables y rápidas.' },
        { title: 'KYC / AML', desc: 'Procesos de cumplimiento con verificación automática.' },
        { title: 'Diversas pasarelas de pago', desc: 'Integramos múltiples proveedores para máxima cobertura.' },
        { title: 'Seguridad transaccional', desc: 'Monitoreo de fraude en tiempo real y tokenización.' },
      ],
      use_cases: [
        { title: 'Banca digital', desc: 'Apps y portales modernos para tus clientes.' },
        { title: 'Onboarding digital', desc: 'Apertura de cuentas 100% remota con KYC automático.' },
        { title: 'Pagos y transferencias', desc: 'Múltiples rails unificados en una sola experiencia.' },
      ],
      methodology: [
        { title: 'Discovery regulatorio', desc: 'Mapeamos exigencias y límites de cumplimiento.' },
        { title: 'Arquitectura segura', desc: 'Diseño listo para auditoría desde el día 1.' },
        { title: 'Integración & test', desc: 'Pruebas exhaustivas contra sandbox y producción.' },
        { title: 'Operación', desc: 'Monitoreo 24/7 y soporte de primera línea.' },
      ],
      tech: ['ISO 20022', 'SWIFT', 'Open Banking', 'KYC/AML', 'PCI-DSS', 'Core-Banking APIs', '3DS'],
    },
    cauracademy: {
      id: 'cauracademy',
      slug: 'cauracademy',
      kicker: '// Cauracademy',
      title: 'Formación ejecutiva y técnica a medida',
      subtitle: 'Unificamos 8+ años de experiencia real en programas que transforman a tu equipo.',
      capabilities: [
        { title: 'Programas ejecutivos', desc: 'Para C-level y decisores — estratégico, sin tecnicismos innecesarios.' },
        { title: 'Upskilling técnico', desc: 'Equipos de desarrollo, seguridad y data con casos reales.' },
        { title: 'Workshops on-site', desc: 'Sesiones hands-on en tus oficinas o de forma remota.' },
        { title: 'Certificaciones', desc: 'Programas formales con evaluación y constancia emitida.' },
      ],
      use_cases: [
        { title: 'Adopción blockchain', desc: 'Del día 0 a proyectos productivos.' },
        { title: 'IA para equipos', desc: 'Uso responsable de LLMs y automatización.' },
        { title: 'Seguridad para developers', desc: 'Secure coding y mentalidad zero-trust.' },
      ],
      methodology: [
        { title: 'Diagnóstico', desc: 'Entendemos el nivel y los objetivos del equipo.' },
        { title: 'Diseño', desc: 'Currículum a la medida con casos de tu industria.' },
        { title: 'Entrega', desc: 'Sesiones sincrónicas + materiales asincrónicos.' },
        { title: 'Seguimiento', desc: 'Acompañamiento post-programa con oficinas de ayuda.' },
      ],
      tech: ['Workshops', 'Hands-on Labs', 'Real Cases', 'Mentoring', 'Certifications'],
    },
  },
  en: {
    platforms: {
      id: 'platforms', slug: 'plataformas',
      kicker: '// Custom Platforms',
      title: 'Enterprise and government software, tailor-made for you',
      subtitle: 'We design, build and operate critical platforms for institutions that cannot fail.',
      capabilities: [
        { title: 'Cloud-native architecture', desc: 'Scalable microservices, containers and Kubernetes orchestration.' },
        { title: 'Legacy integration', desc: 'We connect your existing systems via secure APIs without breaking operations.' },
        { title: 'User-centered design', desc: 'Clear interfaces for operational teams, decision makers and citizens.' },
        { title: 'High availability', desc: '99.9%+ SLA, full observability and contingency plans.' },
      ],
      use_cases: [
        { title: 'Citizen portals', desc: 'Procedures, queries and public services digitized end-to-end.' },
        { title: 'Executive dashboards', desc: 'Real-time visibility of critical KPIs for decision-making.' },
        { title: 'Back-office automation', desc: 'Manual-task reduction with digital flows and reporting.' },
      ],
      methodology: [
        { title: 'Discovery', desc: 'We understand business, stakeholders, risks and dependencies.' },
        { title: 'Architecture', desc: 'We design the solution with security and scalability from day 1.' },
        { title: 'Development', desc: 'Short sprints, continuous demos, strict code review.' },
        { title: 'Operation', desc: 'Documented handoff + 24/7 support if needed.' },
      ],
      tech: ['React', 'FastAPI', 'Node.js', 'PostgreSQL', 'MongoDB', 'Kubernetes', 'Docker', 'API-first', 'Cloud Native'],
    },
    ai: {
      id: 'ai', slug: 'ia',
      kicker: '// AI & Automation',
      title: 'Artificial intelligence applied to critical processes',
      subtitle: 'Models and agents that automate complex flows, reduce errors and free up your team.',
      capabilities: [
        { title: 'Intelligent agents', desc: 'LLMs orchestrated with corporate tools — assisted decisions in real time.' },
        { title: 'Process automation', desc: 'Document, back-office and operation flows with AI + RPA.' },
        { title: 'Custom models', desc: 'Industry-specific models trained on your data — private and under your control.' },
        { title: 'Predictive analytics', desc: 'Forecasting, anomaly detection and intelligent segmentation.' },
      ],
      use_cases: [
        { title: 'KYC / onboarding', desc: 'Automatic verification with OCR + fraud models.' },
        { title: 'Assisted support', desc: 'AI co-pilots for support and sales teams.' },
        { title: 'Document classification', desc: 'Thousands of documents processed per day without fatigue.' },
      ],
      methodology: [
        { title: 'Opportunity', desc: 'We identify where AI pays back fastest.' },
        { title: 'Secure dataset', desc: 'Data curation and anonymization with strict policies.' },
        { title: 'Pilot', desc: 'We measure ROI before scaling — results in weeks.' },
        { title: 'Production', desc: 'Continuous monitoring and periodic re-training.' },
      ],
      tech: ['LLMs', 'Python', 'PyTorch', 'OpenAI / Anthropic', 'Vector DBs', 'LangChain', 'RAG', 'MLOps'],
    },
    security: {
      id: 'security', slug: 'seguridad',
      kicker: '// Gov-Grade Security',
      title: 'Government standards applied to your stack',
      subtitle: 'Zero-Trust, continuous hardening and compliance with the most demanding norms.',
      capabilities: [
        { title: 'Zero-Trust architecture', desc: 'No implicit trust. Authentication and authorization on every request.' },
        { title: 'Compliance', desc: 'ISO 27001, NIST, SOC 2 — we accompany the full certification journey.' },
        { title: 'Hardening & audits', desc: 'Continuous pentesting, code review and infrastructure hardening.' },
        { title: 'Identity management', desc: 'SSO, MFA, granular RBAC and anomaly detection.' },
      ],
      use_cases: [
        { title: 'Financial platforms', desc: 'Protection of transactions and sensitive data.' },
        { title: 'Government systems', desc: 'Citizen data and critical services under lock.' },
        { title: 'Incident response', desc: 'Contingency plans and scheduled drills.' },
      ],
      methodology: [
        { title: 'Assessment', desc: 'We map attack surface and gaps.' },
        { title: 'Roadmap', desc: 'Plan prioritized by impact and effort.' },
        { title: 'Implementation', desc: 'Technical controls + policies + training.' },
        { title: 'Continuous improvement', desc: 'Periodic audits and threat intel.' },
      ],
      tech: ['Zero-Trust', 'OAuth 2.0', 'SAML', 'Vault', 'ISO 27001', 'NIST', 'OWASP', 'SIEM', 'WAF'],
    },
    blockchain: {
      id: 'blockchain', slug: 'blockchain',
      kicker: '// Blockchain & Web3',
      title: 'Our DNA since 2016, applied to any industry',
      subtitle: 'Smart contracts, tokenization and decentralized identity for real use cases.',
      capabilities: [
        { title: 'Auditable smart contracts', desc: 'Designed with secure patterns and formal audits.' },
        { title: 'Tokenization', desc: 'Real-world assets (RWA), equity and on-chain loyalty programs.' },
        { title: 'Decentralized identity', desc: 'DIDs and verifiable credentials without friction.' },
        { title: 'Cross-chain integration', desc: 'Secure bridges and multi-chain when the case requires it.' },
      ],
      use_cases: [
        { title: 'Traceability', desc: 'Supply chains auditable end-to-end.' },
        { title: 'Loyalty programs', desc: 'Tokens with real value for your clients.' },
        { title: 'Public registries', desc: 'Immutable, transparent and verifiable.' },
      ],
      methodology: [
        { title: 'Use case', desc: 'We validate if blockchain adds real value.' },
        { title: 'Architecture', desc: 'Network choice, standards and economic model.' },
        { title: 'Development & audit', desc: 'Secure code + formal audit.' },
        { title: 'Deployment', desc: 'Launch, on-chain monitoring and maintenance.' },
      ],
      tech: ['Solidity', 'Ethereum', 'Bitcoin', 'Polygon', 'IPFS', 'The Graph', 'Hardhat', 'Foundry'],
    },
    banking: {
      id: 'banking', slug: 'banca',
      kicker: '// Banking Solutions',
      title: 'Allies of the national banking sector in Venezuela',
      subtitle: 'Financial technology with secure integrations, compliance and real sector experience.',
      capabilities: [
        { title: 'Core-banking integration', desc: 'APIs to core systems and regulators — reliable and fast.' },
        { title: 'KYC / AML', desc: 'Compliance processes with automatic verification.' },
        { title: 'Multiple payment gateways', desc: 'We integrate multiple providers for maximum coverage.' },
        { title: 'Transactional security', desc: 'Real-time fraud monitoring and tokenization.' },
      ],
      use_cases: [
        { title: 'Digital banking', desc: 'Modern apps and portals for your clients.' },
        { title: 'Digital onboarding', desc: '100% remote account opening with automatic KYC.' },
        { title: 'Payments and transfers', desc: 'Multiple rails unified in a single experience.' },
      ],
      methodology: [
        { title: 'Regulatory discovery', desc: 'We map requirements and compliance limits.' },
        { title: 'Secure architecture', desc: 'Audit-ready design from day 1.' },
        { title: 'Integration & test', desc: 'Extensive testing against sandbox and production.' },
        { title: 'Operation', desc: '24/7 monitoring and first-line support.' },
      ],
      tech: ['ISO 20022', 'SWIFT', 'Open Banking', 'KYC/AML', 'PCI-DSS', 'Core-Banking APIs', '3DS'],
    },
    cauracademy: {
      id: 'cauracademy', slug: 'cauracademy',
      kicker: '// Cauracademy',
      title: 'Tailor-made executive and technical training',
      subtitle: 'We unify 8+ years of real experience into programs that transform your team.',
      capabilities: [
        { title: 'Executive programs', desc: 'For C-level and decision makers — strategic, no unnecessary tech jargon.' },
        { title: 'Technical upskilling', desc: 'Dev, security and data teams with real cases.' },
        { title: 'On-site workshops', desc: 'Hands-on sessions in your offices or remotely.' },
        { title: 'Certifications', desc: 'Formal programs with evaluation and certificate.' },
      ],
      use_cases: [
        { title: 'Blockchain adoption', desc: 'From day 0 to production projects.' },
        { title: 'AI for teams', desc: 'Responsible LLM use and automation.' },
        { title: 'Security for developers', desc: 'Secure coding and zero-trust mindset.' },
      ],
      methodology: [
        { title: 'Diagnosis', desc: "We understand the team's level and goals." },
        { title: 'Design', desc: 'Tailor-made curriculum with cases from your industry.' },
        { title: 'Delivery', desc: 'Synchronous sessions + asynchronous materials.' },
        { title: 'Follow-up', desc: 'Post-program accompaniment with office hours.' },
      ],
      tech: ['Workshops', 'Hands-on Labs', 'Real Cases', 'Mentoring', 'Certifications'],
    },
  },
};

export const bookingCopy = {
  es: {
    nav_back: '← Volver',
    title: 'Agenda una reunión',
    subtitle: 'Reservemos 30 minutos para hablar de tu proyecto. Sin compromiso, siempre bajo confidencialidad.',
    step_labels: ['Servicio', 'Fecha', 'Hora', 'Contacto', 'Listo'],
    service_step: {
      title: '¿Sobre qué te gustaría hablar?',
      subtitle: 'Selecciona el pilar que más te interesa.',
    },
    date_step: {
      title: 'Elige una fecha',
      subtitle: 'Horarios disponibles en los próximos 14 días hábiles.',
    },
    time_step: {
      title: 'Elige un horario',
      subtitle: 'Todas las horas están en GMT-4 (Caracas). Duración: 30 min.',
      unavailable: 'Ocupado',
    },
    form_step: {
      title: 'Tus datos',
      subtitle: 'Completaremos la reserva y te llegará una confirmación.',
      labels: { name: 'Nombre', email: 'Email', company: 'Empresa (opcional)', phone: 'Teléfono (opcional)', notes: 'Contexto (opcional)' },
      placeholders: { name: 'María Pérez', email: 'tu@email.com', company: 'Tu empresa', phone: '+58 412 123 4567', notes: 'Cuéntanos brevemente sobre tu proyecto...' },
    },
    success: {
      title: '¡Reserva confirmada!',
      subtitle: 'Te contactaremos para confirmar por email. Gracias por tu interés en Cauratech.',
      cta: 'Volver al inicio',
    },
    actions: { next: 'Continuar', back: 'Atrás', submit: 'Confirmar reunión' },
    errors: { required: 'Campo requerido', submit: 'No pudimos crear la reserva. Inténtalo de nuevo.' },
    days_short: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    months: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
  },
  en: {
    nav_back: '← Back',
    title: 'Book a meeting',
    subtitle: "Let's reserve 30 minutes to talk about your project. No commitment, always under confidentiality.",
    step_labels: ['Service', 'Date', 'Time', 'Contact', 'Done'],
    service_step: {
      title: 'What would you like to talk about?',
      subtitle: 'Select the pillar that interests you most.',
    },
    date_step: {
      title: 'Pick a date',
      subtitle: 'Available slots within the next 14 business days.',
    },
    time_step: {
      title: 'Pick a time',
      subtitle: 'All times in GMT-4 (Caracas). Duration: 30 min.',
      unavailable: 'Booked',
    },
    form_step: {
      title: 'Your details',
      subtitle: "We'll complete the booking and you'll receive a confirmation.",
      labels: { name: 'Name', email: 'Email', company: 'Company (optional)', phone: 'Phone (optional)', notes: 'Context (optional)' },
      placeholders: { name: 'Jane Doe', email: 'you@email.com', company: 'Your company', phone: '+1 555 123 4567', notes: 'Briefly tell us about your project...' },
    },
    success: {
      title: 'Booking confirmed!',
      subtitle: "We'll contact you to confirm by email. Thanks for your interest in Cauratech.",
      cta: 'Back to home',
    },
    actions: { next: 'Continue', back: 'Back', submit: 'Confirm meeting' },
    errors: { required: 'Required field', submit: 'We could not create the booking. Please try again.' },
    days_short: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  },
};

export const servicePageCopy = {
  es: {
    back_to_home: '← Inicio',
    back_to_services: 'Ver todos los servicios',
    capabilities: 'Capacidades clave',
    use_cases: 'Casos de uso',
    methodology: 'Cómo trabajamos',
    tech: 'Tecnologías',
    cta_title: '¿Listo para avanzar al futuro?',
    cta_subtitle: 'Reservemos 30 minutos para explorar tu caso.',
    cta_button: 'Agenda una reunión',
  },
  en: {
    back_to_home: '← Home',
    back_to_services: 'View all services',
    capabilities: 'Key capabilities',
    use_cases: 'Use cases',
    methodology: 'How we work',
    tech: 'Technologies',
    cta_title: 'Ready to advance to the future?',
    cta_subtitle: "Let's reserve 30 minutes to explore your case.",
    cta_button: 'Book a meeting',
  },
};
